/**
 * App open splash — one branded screen (logo + text), then dismiss.
 * Native: Android 12 system splash is solid color only; this HTML layer is the real splash.
 * Web: same branded splash until first paint.
 */
import { Capacitor } from '@capacitor/core';

const SPLASH_MIN_MS = 420;
const SPLASH_FADE_MS = 220;

let startedAt = 0;
let finished = false;

export function markBootSplashStarted(): void {
  startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
}

async function hideNativeSplash(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide({ fadeOutDuration: SPLASH_FADE_MS });
  } catch {
    /* plugin optional in web builds */
  }
}

function getSplashEl(): HTMLElement | null {
  return document.getElementById('boot-splash');
}

/** Play exit animation and remove the HTML splash layer / hide native splash. */
export async function dismissBootSplash(): Promise<void> {
  if (finished) return;
  finished = true;

  const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - (startedAt || 0);
  const wait = Math.max(0, SPLASH_MIN_MS - elapsed);
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));

  if (Capacitor.isNativePlatform()) {
    const el = getSplashEl();
    if (el) el.remove();
    await hideNativeSplash();
    return;
  }

  await hideNativeSplash();

  const el = getSplashEl();
  if (!el) return;

  el.classList.add('boot-splash--exit');
  await new Promise((r) => setTimeout(r, SPLASH_FADE_MS));
  el.remove();
}
